/**
 * @author Ashish Dwivedi <ashish.dwivedi@314ecorp.com>
 * @description Add Payer Button
 */
import { ButtonProps, Button, App, Tooltip } from 'antd';
import _ from 'lodash';
import { Plus } from '@icon-park/react';
import { useNavigate, useLocation } from 'react-router-dom';

import { MODAL_TYPES } from '../modal';
import useAddPayer from 'hooks/admin/mutate/useAddPayer';
import useUpdateEnrollmentData from 'hooks/admin/mutate/useUpdateEnrollmentData';
import { useAppStoreValues } from 'store/app';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';
import { getEditPayerDetailsPath, getEligibilityNewRequestPath } from 'utils/router';
import { isEkycCompletedNpi } from 'utils/admin/npi';
import { ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES } from 'app-constants/eligibility';
import { usePersistenceStoreValues, usePersistenceStoreActions } from 'store/persistence';
import { usePayerStoreActions } from 'store/payers';

export interface AddPayerProps extends ButtonProps {
	payer: Payers.IRegisteredPayer;
	providers: Provider.IPaginatedProviderMeta;
	tenantPayerId?: string;
}

const AddPayer: React.FC<AddPayerProps> = ({ payer, tenantPayerId, providers, ...props }) => {
	const navigate = useNavigate();
	const { canUpdatePayers } = useAppStoreValues().userAccess;
	const { state } = useLocation();
	const fromEligibility = (state as { fromEligibility?: boolean })?.fromEligibility;
	const { mutateAsync, isPending } = useAddPayer();
	const { modal } = App.useApp();
	const { mutate: updateEnrollmentData } = useUpdateEnrollmentData();
	const { formValues } = usePersistenceStoreValues();
	const { setFormValues } = usePersistenceStoreActions();
	const { updateRemountTriggerKey } = usePayerStoreActions();

	// auto request enrollment for payer when there is single provider with eKYC completed status
	const requestEnrollment = () => {
		updateEnrollmentData({
			payerID: payer.id,
			userModel: [
				{
					providerId: _.first(providers?.data)?.id,
					enrollmentStatus: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested,
				},
			],
		});
	};

	const handlePayerAdded = () => {
		modal.confirm({
			...MODAL_TYPES.confirm,
			title: 'Payer Added',
			content: (
				<>
					{`${payer.name} has been added to your payer list. Would you like to add another payer or return to the eligibility form?`}
				</>
			),
			okText: 'Return to Eligibility',
			cancelText: 'Add Another Payer',
			onOk: () => {
				setFormValues({ ...formValues, payer: payer.id });
				navigate(getEligibilityNewRequestPath());
			},
			onCancel: () => updateRemountTriggerKey(),
		});
	};

	const handleAddPayer = () => {
		const ekycCompletedProviders = _.filter(providers?.data, (provider) =>
			isEkycCompletedNpi(provider?.ekycDetails?.verificationStatus),
		);
		const hasSingleEkycCompletedProvider = _.size(ekycCompletedProviders) === 1;
		const firstProvider = _.first(ekycCompletedProviders);
		const autoRequestEnrollment = hasSingleEkycCompletedProvider && firstProvider?.tin;
		// if payer is enrollment required and auto request enrollment is not possible, navigate to edit payer details page
		if (payer.enrollmentRequired && !autoRequestEnrollment) {
			navigate(getEditPayerDetailsPath(tenantPayerId));
			return;
		}
		// else
		modal.confirm({
			...MODAL_TYPES.confirm,
			content: 'Are you sure you want to add this payer?',
			okText: 'Add',
			onOk: () =>
				mutateAsync(
					{ id: payer.id },
					{
						onSuccess: (data) => {
							if (data?.enrollmentRequired) {
								requestEnrollment();
							}
							if (fromEligibility && !data?.enrollmentRequired) {
								handlePayerAdded();
							}
						},
					},
				),
		});
	};

	return (
		<Tooltip title={`${canUpdatePayers ? 'Add payer' : NO_ACCESS_TOOLTIP_TEXT}`}>
			<Button color='default' disabled={isPending || !canUpdatePayers} {...props} onClick={handleAddPayer} icon={<Plus />} />
		</Tooltip>
	);
};

export default AddPayer;
